import { Loader } from "@/components/loader/Loader.tsx";
import { api } from "@/lib/http.ts";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocation } from "wouter";

type ReferralLandingProps = { code: string };

/** Remembers a referral code on the API origin for the signup that follows. */
function useRecordReferral() {
  return useMutation({
    mutationFn: async (referralCode: string): Promise<void> => {
      await api.post<void>(`/api/p/referrals/${encodeURIComponent(referralCode)}`);
    },
  });
}

/**
 * Where a referral link (`/r/<code>`) lands. Hands the code to the API, which
 * remembers it for the signup that follows; this page then sends the visitor
 * on to sign-in whether or not that worked. The API sets that cookie on its
 * own origin, the same one the sign-in buttons go through, so the code
 * survives the OAuth round trip.
 */
export const ReferralLanding = ({ code }: ReferralLandingProps) => {
  const [, setLocation] = useLocation();
  const { mutate } = useRecordReferral();

  useEffect(() => {
    // Per call rather than on the mutation, so a POST that settles after the
    // visitor has already left this page doesn't pull them to sign-in.
    mutate(code, { onSettled: () => setLocation("/auth", { replace: true }) });
  }, [code, mutate, setLocation]);

  return <Loader />;
};
